import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Card from "../cards";
import api from "../../../services/api";
import CircularLoader from "../../UI/Loader";
import Chart from "./Chart";
import ColumnChart from "./BarChart";
import BookingsChart from "./BookingsChart";
import BookingRequestsChart from "./BookingRequestsChart";
import { COLORS } from "../../../assets/colors";
import { LuUsers, LuCar, LuClipboardList, LuRotateCcw, LuAlertTriangle, LuBan, LuDollarSign, LuShieldCheck } from "react-icons/lu";

function Dashboard() {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        setLoading(true);
        const response = await api.get("admin/dashboard/");
        if (response.status == 200) {
          setLoading(false);
          setDashboardData(response.data);
        }
      } catch (error) {
        setLoading(false);
      }
    }
    fetchDashboardData();
  }, []);

  if (loading) {
    return <CircularLoader isLoading={loading} />;
  }

  const stats = [
    { title: "Active Users (Total)", content: dashboardData.activeUsersTotal || 0, descriptions: "Current online users", icon: <LuUsers />, color: "#3B82F6" },
    { title: "Active Users (Web)", content: dashboardData.activeUsersWeb || 0, descriptions: "Website users", icon: <LuUsers />, color: "#60A5FA" },
    { title: "Active Users (Mobile)", content: dashboardData.activeUsersMobile || 0, descriptions: "Mobile app users", icon: <LuUsers />, color: "#93C5FD" },
    { title: "Total Customers", content: dashboardData.users, descriptions: "Active customers", icon: <LuUsers />, color: COLORS.adminAccent },
    { title: "Total Rides", content: dashboardData.rides, descriptions: "Rides completed", icon: <LuCar />, color: "#818CF8" },
    { title: "Drivers", content: dashboardData.drivers, descriptions: "Published rides", icon: <LuShieldCheck />, color: "#34D399" },
    { title: "Passengers", content: dashboardData.passangers, descriptions: "Travellers", icon: <LuUsers />, color: "#FBBF24" },
    { title: "Bookings", content: dashboardData.bookings, descriptions: "Total Bookings", icon: <LuClipboardList />, color: "#60A5FA" },
    { title: "Vehicles", content: dashboardData.vehicles, descriptions: "Added vehicles", icon: <LuCar />, color: "#A78BFA" },
    { title: "Refunds", content: dashboardData.refunds, descriptions: "Refund items", icon: <LuRotateCcw />, color: "#F87171" },
    { title: "Verifications", content: dashboardData.verificationsCount, descriptions: "Requests", icon: <LuShieldCheck />, color: "#2DD4BF" },
    { title: "Earnings", content: `Rs.${dashboardData.totalEarnings || 0}`, descriptions: "Total earnings", icon: <LuDollarSign />, color: "#10B981" },
    { title: "Banned Users", content: dashboardData.banned, descriptions: "Banned users", icon: <LuBan />, color: COLORS.adminWarning },
    { title: "Reports", content: dashboardData.reports, descriptions: "Received reports", icon: <LuAlertTriangle />, color: COLORS.adminError },
  ];

  return (
    <DashboardWrapper>
      <Header>
        <h1>Dashboard Overview</h1>
        <p>Welcome back! Here's what's happening today.</p>
      </Header>
      
      <StatsGrid>
        {stats.map((stat, index) => (
          <Card
            key={index}
            title={stat.title}
            content={stat.content}
            descriptions={stat.descriptions}
            icon={stat.icon}
            styles={{ 
              borderLeft: `4px solid ${stat.color}`,
            }}
          />
        ))}
      </StatsGrid>

      <ChartsSection>
        <ChartCard>
          <h3>Rides Growth</h3>
          <div className="chart-wrapper">
            <ColumnChart />
          </div>
        </ChartCard>
        
        <ChartCard>
          <h3>User Statistics</h3>
          <div className="chart-wrapper">
            <Chart />
          </div>
        </ChartCard>

        <ChartCard>
          <h3>Booking Trends</h3>
          <div className="chart-wrapper">
            <BookingsChart />
          </div>
        </ChartCard>

        <ChartCard>
          <h3>Request Analysis</h3>
          <div className="chart-wrapper">
            <BookingRequestsChart />
          </div>
        </ChartCard>
      </ChartsSection>
    </DashboardWrapper>
  );
}

export default Dashboard;

const DashboardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const Header = styled.div`
  h1 {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 700;
    color: ${COLORS.adminTextPrimary};
  }
  p {
    margin: 0.5rem 0 0 0;
    color: ${COLORS.adminTextSecondary};
    font-size: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 2rem;
`;

const ChartCard = styled.div`
  background: ${COLORS.adminCardBg};
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: ${COLORS.adminShadow};
  border: 1px solid rgba(0, 0, 0, 0.05);

  h3 {
    margin: 0 0 1.5rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: ${COLORS.adminTextPrimary};
  }

  .chart-wrapper {
    height: 350px;
    width: 100%;
  }
`;
